import React from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '../lib/usePageTitle'

const CONSOLE_URL = 'https://app-bg.planesailing.io'

interface FeatureCard {
  title: string
  body: string
  tag: string
  icon: React.ReactNode
}

const icon = (path: React.ReactNode) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {path}
  </svg>
)

const features: FeatureCard[] = [
  {
    title: 'Orchestrated ceremonies',
    tag: 'Core',
    body: 'Seal, periodic possession proofs, and unseal as one coordinated protocol. Break Glass holds the state so your quorum doesn’t have to.',
    icon: icon(<><circle cx="10" cy="10" r="6" /><path d="M10 6v4l3 2" /></>),
  },
  {
    title: 'Shamir secret sharing',
    tag: 'Cryptography',
    body: 'A data key is split into N shards with a configurable K-of-N threshold. Any K holders can reconstruct; fewer than K reveals nothing.',
    icon: icon(<><path d="M10 2l7 4v8l-7 4-7-4V6z" /><path d="M10 2v16M3 6l14 8M17 6L3 14" /></>),
  },
  {
    title: 'Envelope encryption',
    tag: 'Cryptography',
    body: 'Each shard is independently encrypted to a recipient’s public key. You never handle raw keys — the envelope is assembled and opened for you.',
    icon: icon(<><rect x="3" y="6" width="14" height="10" rx="2" /><path d="M3 7l7 5 7-5" /></>),
  },
  {
    title: 'OIDC identity binding',
    tag: 'Zero trust',
    body: 'Recipients authenticate through your identity provider. Shards are bound to verified people, not shared secrets or spoofable emails.',
    icon: icon(<><path d="M10 2L3 5v5c0 4 3.5 6.5 7 8 3.5-1.5 7-4 7-8V5l-7-3z" /><circle cx="10" cy="8.5" r="1.8" /><path d="M7 14c.4-1.6 1.6-2.4 3-2.4s2.6.8 3 2.4" /></>),
  },
  {
    title: 'Possession proofs',
    tag: 'Health',
    body: 'Holders periodically prove they still possess their shard. Lost devices and departures surface as health signals before an emergency, not during one.',
    icon: icon(<><path d="M4 10l4 4 8-8" /><circle cx="10" cy="10" r="8" strokeOpacity="0.3" /></>),
  },
  {
    title: 'Client-side reconstruction',
    tag: 'Peer-to-peer',
    body: 'Unsealing happens in the requester’s browser. The data key is rebuilt and the secret decrypted locally — the server only ever relayed ciphertext.',
    icon: icon(<><rect x="3" y="4" width="14" height="11" rx="2" /><path d="M7 18h6M10 15v3" /><path d="M8 9l1.5 1.5L13 7" /></>),
  },
  {
    title: 'Tamper-evident audit',
    tag: 'Compliance',
    body: 'Every seal, proof, and unseal request is attributed to an authenticated identity and recorded, giving you a defensible trail of who did what, when.',
    icon: icon(<><rect x="4" y="3" width="12" height="14" rx="2" /><path d="M7 7h6M7 10h6M7 13h3" /></>),
  },
  {
    title: 'Cloudflare-native',
    tag: 'Deploy',
    body: 'The relay runs on Cloudflare Workers with D1 for metadata and ciphertext. Self-host at the edge in minutes, or use the managed service.',
    icon: icon(<><path d="M4 13a4 4 0 010-6 5 5 0 019.8-1A3.5 3.5 0 0117 10H5" /><path d="M6 14h9M8 17h6" /></>),
  },
  {
    title: 'Quorum management',
    tag: 'Operations',
    body: 'Add and remove holders, adjust the threshold, and re-seal when your team changes. Recovery survives reorgs without a fire drill.',
    icon: icon(<><circle cx="6" cy="7" r="2.2" /><circle cx="14" cy="7" r="2.2" /><path d="M2.5 16c0-2 1.6-3.4 3.5-3.4S9.5 14 9.5 16M10.5 16c0-2 1.6-3.4 3.5-3.4s3.5 1.4 3.5 3.4" /></>),
  },
]

export function FeaturesPage() {
  usePageTitle('Features')

  return (
    <div className="page">
      <section className="page-hero narrow reveal">
        <div className="hero-pill">Features</div>
        <h1>Everything the ceremony needs — and nothing that touches your plaintext.</h1>
        <p className="hero-lead">
          Break Glass is a focused tool: it orchestrates the cryptographic ceremony of sealing and
          unsealing a shared secret across a quorum. Here&apos;s the feature set, plus an honest look
          at what&apos;s on the roadmap.
        </p>
      </section>

      <section className="content-section">
        <div className="section-heading reveal">
          <div className="eyebrow">Feature set · What you get</div>
          <h2>A complete ceremony engine, edge-deployable.</h2>
          <p className="muted">
            From Shamir split to client-side reconstruction, every primitive is handled for you — no
            KMS to wire up, no raw keys to mishandle.
          </p>
        </div>
        <div className="feature-grid">
          {features.map((card) => (
            <article className="feature-card reveal" key={card.title}>
              <div className="feature-card-icon">{card.icon}</div>
              <div className="tiny-pill feature-card-pill">{card.tag}</div>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section content-band" id="roadmap">
        <div className="section-heading reveal">
          <div className="tiny-label">Roadmap</div>
          <h2>What&apos;s coming next.</h2>
          <p className="muted">
            We&apos;d rather be clear about a current limitation than oversell. Here&apos;s where
            Break Glass is headed.
          </p>
        </div>
        <div className="roadmap-card reveal">
          <div className="tiny-label">Not yet supported · On the roadmap</div>
          <h3>External-backend ciphertext storage</h3>
          <p>
            Today, shards live with their holders and the opaque relay — Break Glass does{' '}
            <strong>not</strong> yet support storing the secret ciphertext material in an external
            backend such as HashiCorp Vault or Vaultwarden.
          </p>
          <p>
            Pluggable external-backend storage — letting you keep ciphertext in a system you already
            operate while Break Glass continues to orchestrate the ceremony — is planned. The
            zero-plaintext guarantee will hold: any external backend would store opaque ciphertext
            only.
          </p>
        </div>
      </section>

      <section className="content-section">
        <div className="signup-cta reveal">
          <div>
            <div className="eyebrow">Ready when you are</div>
            <h2>Seal your first secret.</h2>
            <p className="muted">Open the console, or self-host the open-source build on Cloudflare.</p>
          </div>
          <div className="cta-actions">
            <a className="button button-solid" href={CONSOLE_URL}>
              Launch console
            </a>
            <Link className="button button-ghost" to="/pricing">
              See pricing
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
